import React, { useEffect, useState } from "react";

const words = [
  { text: "StrataEd has interviewed 100+ teachers, administrators, and policy-makers to understand the PD landscape, and this is what we've heard.", gridArea: "header" },
  { text: '"Boring"', gridArea: "center" },
  { text: '"Monotonous"', gridArea: "top-left" },
  { text: '"Not Reinforced"', gridArea: "top-right" },
  { text: '"Inapplicable"', gridArea: "bottom-left" },
  { text: '"Irrelevant"', gridArea: "bottom-right" }
];

const WordAnimation = ({ word, isVisible, hasAppeared, onComplete }) => {
  const [isFadingIn, setIsFadingIn] = useState(false);

  useEffect(() => {
    if (isVisible && !hasAppeared) {
      setIsFadingIn(true);
      
      // Complete animation after fade in
      const completeTimer = setTimeout(() => {
        onComplete();
      }, 1500);

      return () => {
        clearTimeout(completeTimer);
      };
    }
  }, [isVisible, hasAppeared, onComplete]);

  const getOpacity = () => {
    if (hasAppeared) return 1; // Keep visible once appeared
    if (isFadingIn) return 1;
    return 0;
  };

  const getScale = () => {
    if (hasAppeared) return 1; // Keep normal size once appeared
    if (isFadingIn) return 1;
    return 0.75;
  };

  // Get responsive font size based on word length and position
  const getFontSize = () => {
    if (word.gridArea === "header") {
      return "text-lg sm:text-xl md:text-2xl lg:text-3xl";
    }
    if (word.text.length > 20) {
      return "text-2xl sm:text-3xl md:text-4xl lg:text-5xl";
    }
    return "text-4xl sm:text-5xl md:text-6xl lg:text-8xl";
  };

  return (
    <div
      className={`font-extrabold text-[#4375AB] transition-all duration-1000 ease-in-out text-center leading-relaxed flex items-center justify-center ${getFontSize()}`}
      style={{
        gridArea: word.gridArea,
        opacity: getOpacity(),
        transform: `scale(${getScale()})`,
        zIndex: hasAppeared ? 10 : (isVisible ? 10 : 0)
      }}
    >
      {word.text}
    </div>
  );
};

export default function App() {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [appearedWords, setAppearedWords] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleWordComplete = () => {
    if (currentWordIndex < words.length - 1) {
      setAppearedWords(prev => [...prev, currentWordIndex]);
      setCurrentWordIndex(currentWordIndex + 1);
    } else {
      // All words are done, add the last one
      setAppearedWords(prev => [...prev, currentWordIndex]);
    }
  };

  const handleRestart = () => {
    setCurrentWordIndex(0);
    setAppearedWords([]);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    
    setIsSubmitting(true);
    setSubmitMessage('');
    
    console.log('Attempting to submit email:', email);
    
    try {
      // IMPORTANT: Replace this URL with your actual Google Apps Script deployment URL
      // To get this URL:
      // 1. Go to your Google Apps Script project
      // 2. Click "Deploy" > "New deployment"
      // 3. Choose "Web app" as type
      // 4. Set "Execute as" to "Me" and "Who has access" to "Anyone"
      // 5. Copy the deployment URL
      const scriptUrl = 'https://script.google.com/macros/s/AKfycbz0iV9cxHaP4KaXGYmHOaC8UES-SwBfEFMqrv_9wFObflGSpBI7TzNzRi7BnYRcwpkE/exec';
      // Use URLSearchParams for form data (more reliable than FormData for Google Apps Script)
      const params = new URLSearchParams();
      params.append('email', email);
      
      const response = await fetch(scriptUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Response data:', result);
        
        if (result.success) {
          setSubmitMessage('Thank you! We\'ll be in touch soon.');
          e.target.reset();
        } else {
          setSubmitMessage(`Error: ${result.error || 'Something went wrong'}`);
        }
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
    } catch (error) {
      console.error('Error submitting email:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      
      setSubmitMessage(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="font-sans">
      <style>{`
        @media (max-width: 640px) {
          .word-grid {
            grid-template-areas: 
              "header"
              "top-left"
              "top-right" 
              "center"
              "bottom-left"
              "bottom-right" !important;
            grid-template-columns: 1fr !important;
            grid-template-rows: auto auto auto 1fr auto auto !important;
          }
        }
      `}</style>
      {/* Animation Section */}
      <div className="min-h-screen bg-gradient-to-br from-[#D1E5FF] via-[#88A3C1] to-[#91B6DD] bg-gradient-animate relative overflow-hidden no-select px-4">
        {/* Grid Container for Word Layout */}
        <div 
          className="h-full w-full grid gap-4 p-4 pb-32 pt-20 word-grid"
          style={{
            gridTemplateAreas: `
              "header header"
              "top-left top-right"
              "center center"
              "bottom-left bottom-right"
            `,
            gridTemplateRows: "auto auto 1fr auto",
            gridTemplateColumns: "1fr 1fr"
          }}
        >
          {/* Word animations */}
          {words.map((word, index) => (
            <WordAnimation
              key={`word-${index}`}
              word={word}
              isVisible={index === currentWordIndex}
              hasAppeared={appearedWords.includes(index)}
              onComplete={handleWordComplete}
            />
          ))}
        </div>

        {/* Progress indicator */}
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {words.map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                appearedWords.includes(index)
                  ? 'bg-[#4375AB] opacity-75'
                  : index === currentWordIndex
                  ? 'bg-[#4375AB] scale-125'
                  : 'bg-[#4375AB] opacity-30'
              }`}
            />
          ))}
        </div>
        
        {/* Skip button */}
        <button
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          className="absolute top-8 right-8 text-[#4375AB] hover:text-[#88A3C1] transition-colors duration-300 font-medium hover:underline z-20"
        >
          Skip to StrataEd
        </button>
        
        {/* Scroll indicator */}
        {appearedWords.length === words.length && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center z-20">
            <p className="text-[#4375AB] font-medium mb-2">Scroll down to see our solution</p>
            <div className="animate-bounce">
              <svg className="w-6 h-6 mx-auto text-[#4375AB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Solution Section */}
      <div className="min-h-screen bg-[#EBF3FF] flex flex-col items-center justify-center px-4 py-8 transition-all duration-1000 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-6xl font-extrabold text-[#4375AB] mb-6 text-center">
            StrataEd
          </h1>
          <h2 className="text-3xl font-bold text-[#4375AB] mb-8 text-center">
            Reinventing Teacher Professional Development
          </h2>
          
          <div className="modern-card rounded-xl p-8 mb-8">
            <p className="text-[#4375AB] mb-6 leading-relaxed">
              StrataEd aims to reinvent teacher professional development (PD) through hyper-personalized PD modules that reinforce continuous learning for teachers. 
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-[#D1E5FF] p-4 rounded-lg">
                <h3 className="font-bold text-[#4375AB] mb-2">The Problem</h3>
                <p className="text-sm text-[#4375AB]">PD is monotonous, repetitive, and often irrelevant to many teachers in the room.</p>
              </div>
              <div className="bg-[#A8C4E8] p-4 rounded-lg">
                <h3 className="font-bold text-[#4375AB] mb-2">The Gap</h3>
                <p className="text-sm text-[#4375AB]">No infrastructure ensures teachers apply valuable lessons in the classroom.</p>
              </div>
              <div className="bg-[#7FA3D3] p-4 rounded-lg">
                <h3 className="font-bold text-[#4375AB] mb-2">Our Solution</h3>
                <p className="text-sm text-[#4375AB]">Personalized modules that evolve with teacher feedback and ensure application.</p>
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-[#4375AB] mb-4">How It Works: The Stratum</h3>
            <p className="text-[#4375AB] mb-6 leading-relaxed">
              A module, or Stratum, is a four- to six-week PD program split into three segments: <strong>learning</strong>, <strong>application</strong>, and <strong>reflection</strong>.
            </p>
            
            <div className="space-y-6 mb-6">
              <div className="flex items-start">
                <div className="step-number mr-4 mt-1">1</div>
                <div>
                  <h4 className="font-bold text-[#4375AB] text-lg mb-2">Learning</h4>
                  <p className="text-[#4375AB] text-sm leading-relaxed">Bridges the gap between academic research and classroom application, focusing on techniques teachers can actually use.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="step-number mr-4 mt-1">2</div>
                <div>
                  <h4 className="font-bold text-[#4375AB] text-lg mb-2">Application</h4>
                  <p className="text-[#4375AB] text-sm leading-relaxed">Teachers integrate new techniques into lesson plans for 1-3 weeks, tracking results and student engagement.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="step-number mr-4 mt-1">3</div>
                <div>
                  <h4 className="font-bold text-[#4375AB] text-lg mb-2">Reflection</h4>
                  <p className="text-[#4375AB] text-sm leading-relaxed">Teachers reflect on efficacy, improve application, and provide feedback to enhance future modules.</p>
                </div>
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-[#4375AB] mb-4">Current Implementation</h3>
            <p className="text-[#4375AB] mb-6 leading-relaxed">
              StrataEd will be working with schools in Indiana, Connecticut, and Colorado starting in the 2025-26 school year, impacting thousands of students. For example, StrataEd is currently working with Rockville High School in Vernon, Connecticut, helping high school math teachers reinforce students' past learning when returning from summer break, followed by flipped classroom methods to improve mathematical conversation.
            </p>
            
            <h3 className="text-xl font-bold text-[#4375AB] mb-4">Long-term Vision</h3>
            <p className="text-[#4375AB] mb-6 leading-relaxed">
              We're building a superstructure of PD modules that can be utilized and modified in perpetuity. Teachers become fellow researchers, designing small-scale studies through our modules to constantly improve education across the country.
          </p>
          </div>
          
          <div className="text-center">
            <h3 className="text-2xl font-bold text-[#4375AB] mb-4">Ready to Transform Teacher PD?</h3>
            <p className="text-[#4375AB] mb-6 max-w-md mx-auto">
              Enter your email to learn more about how StrataEd can revolutionize professional development in your school.
            </p>
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 w-full max-w-md mx-auto mb-8">
              <input
                type="email"
                name="email"
                placeholder="Your email"
                className="modern-input w-full"
                required
                disabled={isSubmitting}
              />
              <button
                type="submit"
                className="tech-button text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Learn More'}
              </button>
            </form>
            
            {submitMessage && (
              <div className={`text-center mb-4 p-3 rounded-lg ${
                submitMessage.includes('Error') 
                  ? 'bg-red-100 text-red-700 border border-red-300' 
                  : 'bg-green-100 text-green-700 border border-green-300'
              }`}>
                {submitMessage}
              </div>
            )}
            
            <button
              onClick={handleRestart}
              className="text-[#4375AB] hover:text-[#88A3C1] transition-colors duration-300 font-medium hover:underline"
            >
              Watch Animation Again
            </button>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-[#4375AB] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Have Questions?</h3>
          <p className="text-lg mb-4">
            We'd love to hear from you! Reach out to us for any questions about StrataEd.
          </p>
          <div className="flex items-center justify-center space-x-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <a 
              href="mailto:trystrataed@gmail.com" 
              className="text-xl font-semibold hover:text-[#D1E5FF] transition-colors duration-300 underline"
            >
              trystrataed@gmail.com
            </a>
          </div>
          <p className="text-sm mt-4 opacity-90">
            We'll get back to you within 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
}
